/**
 * SnapshotPreviewContent — content and diff views for snapshot
 *
 * Displays either snapshot content or diff based on comparisonMode (switched via Header tabs).
 */

import { useT } from "@magic-md/i18n/react";
import { useEffect, useState } from "react";
import type { SnapshotMeta } from "@magic-md/engine-client/protocol";
import { cn } from "../../lib/utils";
import { SnapshotCodeViewer } from "./SnapshotCodeViewer";

export interface SnapshotPreviewContentProps {
    snapshot: SnapshotMeta;
    snapshotId: string;
    filePath: string;
    comparisonMode: "content" | "diff";
    loadContent: (snapshotId: string) => Promise<string | null>;
    loadDiff: (snapshotId: string, currentContent: string) => Promise<string | null>;
}

export function SnapshotPreviewContent({
    snapshot: _snapshot,
    snapshotId,
    comparisonMode,
    loadContent,
    loadDiff,
}: SnapshotPreviewContentProps) {
    const t = useT("editor");
    const [content, setContent] = useState<string | null>(null);
    const [diff, setDiff] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get the active Editor document content (from platform service if available)
    // For now, using empty string as placeholder
    // TODO: wire to actual current document content
    const getCurrentDocContent = async () => {
        return ""; // placeholder
    };

    // Load content when snapshot changes or mode changes
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                if (comparisonMode === "content") {
                    const loaded = await loadContent(snapshotId);
                    if (loaded) {
                        setContent(loaded);
                    } else {
                        console.error(`[SnapshotPreviewContent] loadContent returned null/falsy for snapshotId=${snapshotId}`);
                        setError("Failed to load snapshot content");
                    }
                } else {
                    const currentContent = await getCurrentDocContent();
                    const loaded = await loadDiff(snapshotId, currentContent);
                    setDiff(loaded);
                    if (!loaded) {
                        console.warn(`[SnapshotPreviewContent] loadDiff returned null/falsy for snapshotId=${snapshotId}`);
                        setError("Failed to load diff");
                    }
                }
            } catch (err) {
                console.error(`[SnapshotPreviewContent] Error loading snapshot ${snapshotId}:`, err);
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [snapshotId, comparisonMode, loadContent, loadDiff]);

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Content area */}
            <div className="flex-1 min-h-0 overflow-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span className="text-xs">{t("status.loading")}...</span>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500">
                        <span className="text-xs">{error}</span>
                    </div>
                ) : comparisonMode === "content" ? (
                    content ? (
                        <SnapshotCodeViewer content={content} />
                    ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                            <span className="text-xs">{t("preview.noContent")}</span>
                        </div>
                    )
                ) : diff ? (
                    <div className="p-3 font-mono text-[11px] bg-background">
                        <pre className="whitespace-pre-wrap break-words leading-relaxed">
                            {diff.split('\n').map((line, i) => {
                                const isAdd = line.startsWith('+') && !line.startsWith('+++');
                                const isRemove = line.startsWith('-') && !line.startsWith('---');
                                const isHeader = line.startsWith('@@');
                                return (
                                    <div
                                        key={i}
                                        className={cn(
                                            "px-2",
                                            isAdd && "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
                                            isRemove && "bg-red-500/15 text-red-700 dark:text-red-400",
                                            isHeader && "bg-blue-500/15 text-blue-700 dark:text-blue-400",
                                            !isAdd && !isRemove && !isHeader && "text-muted-foreground"
                                        )}
                                    >
                                        {line}
                                    </div>
                                );
                            })}
                        </pre>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        <span className="text-xs">{t("preview.noDiff")}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
